import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ProgramDialog, SavedProgramsContext, AppliedProgramsContext } from '../course-cards';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isProgramDialogOpen, setIsProgramDialogOpen] = useState(false);
  const [programLoading, setProgramLoading] = useState(false);
  // Context state for program components
  const [savedPrograms, setSavedPrograms] = useState({});
  const [appliedPrograms, setAppliedPrograms] = useState({});

  // Status options with integer values
  const statusOptions = [
    { value: 0, label: 'Pending' },
    { value: 1, label: 'Sent to University' },
    { value: 2, label: 'Offer Received' },
    { value: 3, label: 'Documents Pending' },
    { value: 4, label: 'All Done' }
  ];

  // Helper function to get status label
  const getStatusLabel = (statusValue) => {
    const status = statusOptions.find(option => option.value === statusValue);
    return status ? status.label : 'Unknown';
  };

  // Helper function to get status color class
  const getStatusColorClass = (statusValue) => {
    switch(statusValue) {
      case 0: // Pending
        return 'bg-amber-100 text-amber-800';
      case 1: // Sent to University
        return 'bg-blue-100 text-blue-800';
      case 2: // Offer Received
        return 'bg-emerald-100 text-emerald-800';
      case 3: // Documents Pending
        return 'bg-orange-100 text-orange-800';
      case 4: // All Done
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper functions for context
  const setSavedStatus = (programId, isSaved) => {
    setSavedPrograms(prev => ({
      ...prev,
      [programId]: isSaved
    }));
  };
  
  const setAppliedStatus = (programId, isApplied) => {
    setAppliedPrograms(prev => ({
      ...prev,
      [programId]: isApplied
    }));
  };
  
  // Context values
  const savedProgramsValue = { savedPrograms, setSavedStatus };
  const appliedProgramsValue = { appliedPrograms, setAppliedStatus };

  const handleLogin = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`,
        { password }
      );
      
      if (response.data.success) {
        setIsAuthenticated(true);
        fetchApplications();
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Authentication failed');
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchApplications = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/applications`
      );
      
      if (response.data.success) {
        setApplications(response.data.applications);
      } else {
        setError('Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('An error occurred while fetching applications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    // Convert string to number
    const statusValue = parseInt(newStatus, 10);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/update-status`,
        { applicationId, status: statusValue }
      );
      
      if (response.data.success) {
        // Update local state
        setApplications(prevApplications => 
          prevApplications.map(app => 
            app._id === applicationId 
              ? { ...app, status: statusValue } 
              : app
          )
        );
        toast.success('Status updated successfully');
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('An error occurred while updating status');
    }
  };

  const fetchProgramDetails = async (programId) => {
    if (!programId) return;
    
    setProgramLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/program/${programId}`
      );
      
      if (response.data.success) {
        setSelectedProgram(response.data.program);
        setIsProgramDialogOpen(true);
      } else {
        toast.error('Failed to fetch program details');
      }
    } catch (error) {
      console.error('Error fetching program details:', error);
      toast.error('Failed to load program details');
    } finally {
      setProgramLoading(false);
    }
  };

  // Render login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h1 className="text-2xl font-bold mb-6 text-center">Admin Login</h1>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter admin password"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={passwordLoading}
              className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              {passwordLoading ? 'Authenticating...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          onClick={fetchApplications}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <SavedProgramsContext.Provider value={savedProgramsValue}>
      <AppliedProgramsContext.Provider value={appliedProgramsValue}>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Application Management</h1>
            <div className="flex space-x-2">
              <button 
                onClick={fetchApplications}
                className="px-4 py-2 text-blue-500 rounded-md flex items-center hover:underline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <button 
                onClick={() => setIsAuthenticated(false)}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>

          {applications.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                    </svg>
                  </div>
                  <input
                    type="search"
                    className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by name, email, phone or university..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b text-left">Name</th>
                    <th className="py-2 px-4 border-b text-left">Email</th>
                    <th className="py-2 px-4 border-b text-left">Phone</th>
                    <th className="py-2 px-4 border-b text-left">Program</th>
                    <th className="py-2 px-4 border-b text-left">Documents</th>
                    <th className="py-2 px-4 border-b text-left">Created At</th>
                    <th className="py-2 px-4 border-b text-left">Status / Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications
                    .filter(application => 
                      application.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      application.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      application.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      application.universityName?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((application) => (
                    <tr key={application._id} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border-b">{application.name}</td>
                      <td className="py-2 px-4 border-b">{application.email}</td>
                      <td className="py-2 px-4 border-b">{application.phone}</td>
                      <td className="py-2 px-4 border-b">
                        {application.programId ? (
                          <button
                            onClick={() => fetchProgramDetails(application.programId)}
                            className="text-blue-500 hover:underline cursor-pointer max-w-[200px]"
                            disabled={programLoading}
                          >
                            {application.universityName}
                            <p className="text-sm text-red-500">{application.programName}</p>
                          </button>
                        ) : (
                          <span>No program information</span>
                        )}
                      </td>
                      <td className="py-2 px-4 border-b">
                        <div className="flex flex-col space-y-1">
                          {application.tenthMarks ? (
                            application.tenthMarks === "Not provided" ? 
                              <span className="text-gray-500">10th Marks</span> :
                              <a href={application.tenthMarks} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">10th Marks</a>
                          ) : null}
                          {application.twelfthMarks ? (
                            application.twelfthMarks === "Not provided" ? 
                              <span className="text-gray-500">12th Marks</span> :
                              <a href={application.twelfthMarks} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">12th Marks</a>
                          ) : null}
                          {application.graduationMarks ? (
                            application.graduationMarks === "Not provided" ? 
                              <span className="text-gray-500">Graduation Marks</span> :
                              <a href={application.graduationMarks} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Graduation Marks</a>
                          ) : null}
                          {application.graduationDegree ? (
                            application.graduationDegree === "Not provided" ? 
                              <span className="text-gray-500">Graduation Degree</span> :
                              <a href={application.graduationDegree} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Graduation Degree</a>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 px-4 border-b">{new Date(application.createdAt).toLocaleDateString()}</td>
                      <td className="py-2 px-4 border-b">
                        <div className="flex flex-col items-center space-y-2">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColorClass(application.status)}`}>
                            {getStatusLabel(application.status)}
                          </span>
                          <select
                            value={application.status !== undefined ? application.status : 0}
                            onChange={(e) => handleStatusChange(application._id, e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            {statusOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Using the ProgramDialog from course-cards.jsx */}
          {isProgramDialogOpen && (
            <ProgramDialog 
              isOpen={isProgramDialogOpen}
              onClose={() => setIsProgramDialogOpen(false)}
              card={selectedProgram}
              isAdmin={true}
            />
          )}
        </div>
      </AppliedProgramsContext.Provider>
    </SavedProgramsContext.Provider>
  );
};

export default AdminApplications; 